import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  Link,
  linkOptions,
  Outlet,
  redirect,
  useLocation,
} from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { type } from "arktype";
import {
  AlertCircle,
  AlertTriangle,
  Archive,
  Calendar,
  CircleCheckBig,
  CircleDot,
  CircleMinus,
  Coins,
  CreditCard,
  DollarSign,
  EllipsisVertical,
  FolderSearch,
  Grid2X2,
  RefreshCcw,
  Settings,
  Split,
  Type,
  Upload,
} from "lucide-react";
import { Suspense, useState } from "react";
import {
  capitalize,
  categoryDisplayNames,
  formatCurrency,
  isOfflinePlatform,
  isTransactionValid,
} from "shared";
import { toast } from "sonner";
import { createColumnConfigHelper } from "@/components/data-table-filter/core/filters";
import type { FiltersState } from "@/components/data-table-filter/core/types";
import { Page } from "@/components/page";
import { SheetSkeleton } from "@/components/skeletons/sheet-skeleton";
import { RenderStatusBadge } from "@/components/table/cells/RenderStatusBadge";
import {
  DashedOutCell,
  RenderCell,
} from "@/components/table/cells/render-cell";
import { DataTable } from "@/components/table/DataTable";
import { TablePagination } from "@/components/table/DataTablePagination";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { usePersistedReactTable } from "@/hooks/use-persisted-table";
import { useRequiredRole } from "@/hooks/useRequiredRole";
import { restrictRoute } from "@/lib/routeBlocker";
import type { RouterOutputs } from "@/lib/trpcClient";
import {
  BulkTransactionAssignField,
  SingleTransactionAssignField,
} from "@/routes/_authed/_org/transactions/-components/transaction-budget-assign";
import { BulkAccountingFieldAssign } from "./accounting/--components/bulk-accounting-field";
import { BulkMarkReady } from "./accounting/--components/bulk-mark-ready";
import { ManualAccountingSync } from "./accounting/--components/manual-sync";
import { SingleMarkReady } from "./accounting/--components/single-mark-ready";
import { AccountingSyncAction } from "./accounting/--components/sync-action";
import { AccountingFieldForm } from "./transactions/-components/accounting/accounting-field-form";
import { ApprovalStatusBadge } from "./transactions/-components/approval-status-badge";

export type AccountingTransaction =
  RouterOutputs["accounting"]["unsyncedTransactions"][number];
const dtf = createColumnConfigHelper<AccountingTransaction>();

const validateSearch = type({
  "callbackError?": "string",
});

export const Route = createFileRoute("/_authed/_org/accounting")({
  beforeLoad: async ({ context }) => {
    const accountingData = await context.queryClient.ensureQueryData(
      context.trpc.accounting.get.queryOptions(),
    );

    const session = await context.queryClient.ensureQueryData(
      context.trpc.auth.session.queryOptions(),
    );

    if (
      accountingData?.status !== "ready" ||
      !session.org?.features.includes("accounting")
    ) {
      throw redirect({
        to: "/accounting-setup",
      });
    }

    restrictRoute(context, {
      redirect: {
        to: "/dashboard",
      },
      requiredRole: "viewOnlyAdmin",
    });

    await context.queryClient.ensureQueryData(
      context.trpc.accounting.getAccountingFields.queryOptions(),
    );
  },
  component: RouteComponent,

  loader: ({ context: { queryClient, trpc } }) => {
    queryClient.ensureQueryData(
      trpc.accounting.unsyncedTransactions.queryOptions(),
    );
    queryClient.ensureQueryData(trpc.accounting.syncHistory.queryOptions());
  },
  validateSearch,
});

type View = "Overview" | "Review" | "Ready";
const initialFilterState = [
  {
    columnId: "SyncStatus",
    hide: true,
    operator: "is",
    type: "option",
    values: ["not ready"],
  },
];

function RouteComponent() {
  const { trpc } = Route.useRouteContext();
  const location = useLocation();
  const navigate = Route.useNavigate();
  const { callbackError } = Route.useSearch();

  const { data: fields } = useSuspenseQuery(
    trpc.accounting.getAccountingFields.queryOptions(),
  );

  const { data: transactions, isLoading: isTransactionsLoading } = useQuery(
    trpc.accounting.unsyncedTransactions.queryOptions(),
  );

  const { data: orgMembers, isLoading: isUsersLoading } = useQuery(
    trpc.org.users.queryOptions(),
  );

  const isLoading = isTransactionsLoading || isUsersLoading;

  const { bookkeeper } = useRequiredRole();

  if (callbackError) {
    toast.error("Error", {
      description: callbackError,
    });
    navigate({
      search: {
        callbackError: undefined,
      },
    });
  }

  const ready = transactions?.filter(
    (trx) => trx.accountingData.status === "ready",
  ).length;
  const notReady = transactions?.filter(
    (trx) => trx.accountingData.status === "not ready",
  ).length;

  const tableColumns: ColumnDef<AccountingTransaction>[] = [
    {
      accessorFn: (row) => row.merchant?.name,
      cell: ({ row: { original } }) => (
        <RenderCell
          merchant={original.merchant}
          merchantCategory={original.category}
          type="merchant"
        />
      ),
      id: "Merchant",
    },
    {
      accessorFn: (row) => row.originatedAt,
      cell: ({ row }) => (
        <RenderCell date={row.original.originatedAt} type="date" />
      ),
      enableResizing: true,
      id: "Date",
    },
    {
      accessorFn: (row) => row.amountCents,
      cell: ({ row }) => (
        <RenderCell amount={row.original.amountCents} type="currency" />
      ),
      id: "Amount",
      meta: {
        align: "right",
      },
    },
    {
      accessorFn: (row) => row.userId,
      accessorKey: "Cardholder",
      cell: ({ row }) => {
        const user = orgMembers?.userMap[row.original.userId];
        return user && <RenderCell type="cardHolder" user={user} />;
      },
      id: "Cardholder",
    },
    {
      accessorFn: (row) => row.category,
      cell: () => null,
      id: "Category",
    },
    {
      //* Converting null to "null" helps with sorting
      accessorFn: (row) => row.budgetId ?? "null",
      cell: ({ row: { original } }) => {
        if (!isTransactionValid(original.status)) {
          return (
            <div className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-1 font-medium text-xs">
              <AlertCircle className="text-gray-600" size={14} />
              <span>N/A</span>
            </div>
          );
        }

        return <SingleTransactionAssignField transaction={original} />;
      },
      exportAccessorFn: (row) => row.budget?.name ?? "NOT ASSIGNED",
      id: "Budget",
    },
    {
      accessorFn: (row) => row.policySatisfied,
      cell: ({ row }) => {
        if (!isTransactionValid(row.original.status)) {
          return <CircleMinus className="mr-1.5 size-4 text-gray-800" />;
        }

        if (row.original.policySatisfied) {
          return <CircleCheckBig className="mr-1.5 size-4 text-green-800" />;
        }

        return <AlertTriangle className="mr-1.5 size-4 text-amber-800" />;
      },
      id: "Policy",
    },
    {
      accessorFn: (row) => row.approvalStatus?.status,
      cell: ({ row: { original: row } }) =>
        row.approvalStatus ? (
          <ApprovalStatusBadge {...row.approvalStatus} withAction />
        ) : (
          <DashedOutCell />
        ),
      id: "Approval",
    },
    {
      accessorFn: (row) => row.card?.id,
      cell: ({ row: { original: row } }) =>
        row.card ? (
          <RenderCell card={row.card} type="card" />
        ) : (
          <DashedOutCell />
        ),
      exportAccessorFn: (row) => row.card?.name,
      id: "Card",
    },
    {
      accessorFn: (row) => row.status,
      accessorKey: "Status",
      cell: ({ row }) => <RenderStatusBadge status={row.original.status} />,
      id: "Status",
    },
    {
      accessorFn: (row) => row.accountingData.status,
      id: "SyncStatus",
    },
    ...fields
      .filter(
        (f) =>
          f.options.length > 0 &&
          f.requirementLevel !== "disabled" &&
          f.name !== "payment_account",
      )
      .map((field) => ({
        accessorFn: () => field.displayName || field.name,
        cell: ({ row }: { row: { original: AccountingTransaction } }) => {
          const transaction = row.original;

          if (transaction.accountingData.splits.length > 1)
            return (
              <span className="flex w-full items-center justify-center gap-2 text-neutral-600 text-sm">
                <Split className="size-3.5" /> Split Transaction
              </span>
            );

          return (
            <AccountingFieldForm
              field={field}
              transaction={transaction}
              selection={
                field.assignmentLevel === "topLevel"
                  ? transaction.accountingData.fieldSelections.find(
                      (fs) => fs.accountingFieldId === field.id,
                    )
                  : transaction.accountingData.splits[0].fieldSelections.find(
                      (fs) => fs.accountingFieldId === field.id,
                    )
              }
              splitId={
                field.assignmentLevel === "lineItem"
                  ? row.original.accountingData.splits[0].id
                  : undefined
              }
              hideLabel
              className="min-w-40"
            />
          );
        },
        enableResizing: false,
        id: field.displayName || field.name,
        maxSize: 250,
        minSize: 250,
        size: 250,
      })),
    {
      accessorFn: (row) => row.accountingData.id,
      cell: ({ row }) => (
        <SingleMarkReady accountingTransaction={row.original} />
      ),
      enableResizing: false,
      id: "Accounting",
      maxSize: 170,
      minSize: 170,
      size: 170,
    },
  ];
  const tableName = "accounting";
  const columnPinning = {
    left: ["Merchant"],
    right: ["Accounting"],
  };
  const columnVisibility = {
    Category: false,
    SyncStatus: false,
  };

  const { table, filters, actions, columns, strategy, setTableFilters } =
    usePersistedReactTable({
      columns: tableColumns,
      data: transactions ?? [],
      filterOptions: {
        columnsConfig,
        defaultFilters: initialFilterState,
        strategy: "client",
      },
      getRowId: (row) => row.id.toString(),
      initialState: {
        columnVisibility,
      },
      state: {
        columnPinning,
      },
      tableName,
      version: "v5",
    });

  const [view, setView] = useState<View>(getInitialViewFromFilters(filters));

  const updateSyncStatusFilter = (operator: string, values: string[]) => {
    setTableFilters((filters) =>
      filters.map((filter) =>
        filter.columnId === "SyncStatus"
          ? { ...filter, hide: true, operator, values }
          : filter,
      ),
    );
  };

  const { data: accountingData } = useSuspenseQuery(
    trpc.accounting.get.queryOptions(),
  );
  const isOffline = isOfflinePlatform(accountingData?.type);

  return (
    <Sheet
      onOpenChange={(open) => {
        if (!open) {
          navigate({
            to: "/accounting",
          });
        }
      }}
      open={
        location.pathname !== "/accounting" &&
        location.pathname.includes("/accounting")
      }
    >
      <Page
        heading="Accounting"
        actions={
          <>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost">
                  <EllipsisVertical />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem>
                  <Link
                    to="/accounting/sync-history"
                    className="flex w-full items-center gap-3"
                  >
                    <Archive className="size-4" />
                    View Sync History
                  </Link>
                </DropdownMenuItem>
                {isOffline && (
                  <DropdownMenuItem asChild>
                    <ManualAccountingSync className="flex items-center gap-3">
                      <Upload className="size-4" />
                      Re-import Chart of Accounts
                    </ManualAccountingSync>
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            {bookkeeper && (
              <Link to="/accounting/settings">
                <Button variant={"outline"}>
                  <Settings />
                  <span>Settings</span>
                </Button>
              </Link>
            )}

            {bookkeeper && (
              <AccountingSyncAction
                accountingTransactions={transactions ?? []}
              />
            )}
          </>
        }
        table={{
          actions,
          columns,
          excludeColumnIds: ["SyncStatus", "Category"],
          filters,
          strategy,
          table,
        }}
        tabs={
          <Tabs
            onValueChange={(v) => {
              const view = v as View;
              setView(view);
              // Apply appropriate filter based on view
              switch (view) {
                case "Ready":
                  updateSyncStatusFilter("is", ["ready"]);
                  break;
                case "Review":
                  updateSyncStatusFilter("is", ["not ready"]);
                  break;
              }
            }}
            value={view}
            className="@md/page:w-auto w-full"
          >
            <TabsList
              variant="standard"
              className="h-fit @md/page:w-auto w-full @md/page:flex-row flex-col [&>button]:w-full [&>button]:py-2"
            >
              <TabsTrigger value="Review" count={notReady} variant="standard">
                <div className="flex items-center gap-2">
                  <FolderSearch className="size-3.5 text-neutral-400" />
                  Needs Review
                </div>
              </TabsTrigger>
              <TabsTrigger value="Ready" count={ready} variant="standard">
                <div className="flex items-center gap-2">
                  <RefreshCcw className="size-3.5 text-neutral-400" />
                  Ready to Sync
                </div>
              </TabsTrigger>
            </TabsList>
          </Tabs>
        }
      >
        {/* <pre>{JSON.stringify(orgData)}</pre> */}
        <DataTable
          noResultsComponent={
            <div className="flex h-36 w-full flex-col items-center justify-center space-y-4">
              <span className="text-xl">No Matching Transactions</span>

              <Button onClick={actions?.removeAllFilters} variant={"outline"}>
                Clear Filters
              </Button>
            </div>
          }
          table={table}
          tableName={tableName}
          navOptions={(row) =>
            linkOptions({
              params: {
                id: row.original.id,
              },
              to: "/accounting/$id",
            })
          }
          loading={isLoading}
          className="[&_th]:border-t-0!"
          selectable
        />

        <TablePagination
          tableName={"transactions"}
          table={table}
          leftSlot={
            table.getFilteredSelectedRowModel().rows.length > 0 && (
              <>
                <span className="-mx-2 text-nowrap font-normal text-neutral-600 text-sm">
                  {formatCurrency(
                    table
                      .getFilteredSelectedRowModel()
                      .rows.reduce((acc, t) => acc + t.original.amountCents, 0),
                  )}{" "}
                  USD
                </span>

                <div className="mx-4 h-8 w-px bg-neutral-300 last:hidden" />

                <BulkTransactionAssignField
                  transactions={table
                    .getFilteredSelectedRowModel()
                    .rows.map((r) => r.original)}
                />

                <BulkAccountingFieldAssign
                  accountingTransactions={table
                    .getFilteredSelectedRowModel()
                    .rows.map((r) => r.original)}
                />

                <BulkMarkReady
                  accountingTransactions={table
                    .getFilteredSelectedRowModel()
                    .rows.map((r) => r.original)}
                />
              </>
            )
          }
        />
      </Page>

      <SheetContent
        onEscapeKeyDown={(e) => {
          e.preventDefault();
        }}
        size={
          !location.pathname.includes("/accounting/sync-history")
            ? "large"
            : "default"
        }
      >
        <Suspense
          fallback={
            <div className="px-6">
              <SheetSkeleton />
            </div>
          }
        >
          <Outlet />
        </Suspense>
      </SheetContent>
    </Sheet>
  );
}

// TODO the is any of can be hardcoded even for a single filter option
function getInitialViewFromFilters(filters: FiltersState): View {
  const syncStatusFilter = filters.find(
    (filter) => filter.columnId === "SyncStatus",
  );

  if (!syncStatusFilter || syncStatusFilter.type !== "option") {
    return "Overview";
  }

  const { operator, values } = syncStatusFilter;

  // Check for "Ready" view - single value "ready"
  if (operator === "is" && values.length === 1 && values[0] === "ready") {
    return "Ready";
  }

  // Check for "Review" view - single value "not ready"
  if (operator === "is" && values.length === 1 && values[0] === "not ready") {
    return "Review";
  }

  // Check for "Overview" view - both values present
  if (
    operator === "is any of" &&
    values.length === 2 &&
    values.includes("ready") &&
    values.includes("not ready")
  ) {
    return "Overview";
  }

  // Default fallback
  return "Overview";
}

const columnsConfig = [
  dtf
    .text()
    .id("Merchant")
    .accessor((row) => row.merchant?.description ?? row.merchant?.name)
    .displayName("Merchant")
    .icon(Type)
    .build(),
  dtf
    .option()
    .id("SyncStatus")
    .displayName("HIDDEN")
    .accessor((row) => row.accountingData.status)
    .icon(Type)
    .transformOptionFn((u) => {
      return {
        label: u,
        value: u,
      };
    })
    .hide()
    .build(),

  //* Hold off on this, as the number display does not look good for currencies
  dtf
    .number()
    .id("Amount")
    .accessor((row) => row.amountCents / 100)
    .displayName("Amount")
    .icon(Coins)
    .build(),
  dtf
    .date()
    .id("Date")
    .displayName("Date")
    .accessor((row) => row.originatedAt)
    .icon(Calendar)
    .build(),

  dtf
    .option()
    .id("Budget")
    .accessor(
      (row) =>
        row.budget ?? {
          id: "null",
          name: "Not Assigned",
        },
    )
    .displayName("Budget")
    .icon(DollarSign)
    .transformOptionFn((u) => {
      if (u.id === "null") {
        return {
          icon: <AlertCircle className="text-red-600" size={14} />,
          label: "Not Assigned",
          value: "null",
        };
      }
      return {
        label: u.name,
        value: u.id,
      };
    })
    .build(),
  dtf
    .option()
    .id("Category")
    .accessor((row) => row.category)
    .displayName("Spend Category")
    .icon(Grid2X2)
    .transformOptionFn((u) => {
      return {
        label: categoryDisplayNames[u],
        value: u,
      };
    })
    .build(),
  dtf
    .option()
    .id("Card")
    .accessor((row) => row.card)
    .displayName("Card")
    .icon(CreditCard)
    .transformOptionFn((u) => {
      return {
        label: u.name,
        value: u.id,
      };
    })
    .build(),
  dtf
    .option()
    .id("Status")
    .accessor((row) => row.status)
    .displayName("Status")
    .icon(CircleDot)
    .transformOptionFn((u) => ({
      label: capitalize(u),
      value: u,
    }))
    .build(),
];
